/**
 * Shared auth-form UI helpers (L2 — fix 2026-08-05).
 *
 * shakeAndToast() and validateEmail() were independently copy-pasted, near-
 * byte-identical, into 8 route files across client-saas and client-sse-console
 * (signin/signup/forgot/resethash × 2 apps) — the exact "duplicate helper
 * copied across N files" shape: one small function, one canonical behavior,
 * maintained by hand in 8 places that could silently drift.
 *
 * Depends on the neodigmToast/neodigmUtils globals from the neodigm55 CDN
 * script (see CLAUDE.md — "neodigm55 kept for behavior/components... toasts").
 * Not imported: these are loaded onto `window` by the CDN script tag in each
 * app's index.html, the same way every existing call site already relied on
 * them being globally present rather than imported.
 */

import { MicrocopyManager } from './meta_microcopy_i18n.js';

/**
 * Shake an invalid form field and show a danger toast, then focus it.
 * @param {HTMLElement} element
 * @param {string} message
 */
export function shakeAndToast(element, message) {
  if (typeof neodigmToast !== 'undefined') {
    neodigmToast.q(message, 'danger');
  }

  if (typeof neodigmUtils !== 'undefined') {
    neodigmUtils.shake(`#${element.id}`);
  } else {
    // Fallback if neodigmUtils not available
    element.classList.add('shake');
    setTimeout(() => element.classList.remove('shake'), 300);
  }

  element.focus();
}

// Same pattern already used server-side (magnetPublic.js's EMAIL_RX,
// magnets.js's NOTIFY_EMAIL_RX, redact.js's EMAIL_RX) — kept identical for
// consistency. Stricter than the 8 duplicated copies' original check
// (atCount === 1 && dotCount >= 1), which is deliberate: that check accepted
// a bare "@example.com" (empty local part) as valid, since it has exactly one
// @ and a dot. This regex requires at least one non-@/non-whitespace
// character on both sides, closing that gap.
// Cannot import the shared rule: this file is loaded onto `window` by a CDN script tag
// (see the header), so it has no module graph to resolve @sse/shared through. Same
// category as shared/machfivemagnet.js — unavoidable duplication, kept byte-identical
// to packages/shared's isValidEmail on purpose. Sweep 2026-08-14: pin, do not share.
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate an email field — shake and toast on error.
 * @param {string} email
 * @param {HTMLElement} inputElement
 * @returns {boolean}
 */
export function validateEmail(email, inputElement) {
  if (!email) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_email_required'));
    return false;
  }

  if (!EMAIL_RX.test(email)) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_email_invalid'));
    return false;
  }

  return true;
}
